'use strict';

import DropMeTool from './DropMeTool.js';
import ViewObjectIn3D from './ViewObjectIn3D.js';

const av = Autodesk.Viewing, avu = Autodesk.Viewing.UI, avp = av.Private;
const namespace = AutodeskNamespace('Autodesk.AEC');
const myExtensionName = 'Autodesk.AEC.DropMeExtension';

export default class DropMeExtension extends av.Extension {
    constructor(viewer, options = { teleportOnRelease: true }) {
        super(viewer, options);

        Autodesk.Viewing.EventDispatcher.prototype.apply(this);

        this.teleportOnRelease = options.teleportOnRelease; // teleportOnRelease used by default, not used on mobile apps.
        this.viewer = viewer;
        this._onModelLoaded = this._onModelLoaded.bind(this);
        this._deactivateTool = this._deactivateTool.bind(this);
        this._onViewportDataFetched = this._onViewportDataFetched.bind(this);
        this.onDropOverride = null;
        this.options = options;
        this.options.triggerDeactivate = this._deactivateTool;
        this.toggleIndex = options.toggleIndex || 0; // at which position at the toolbar to toggle DropMe button
        this.enableGuidance = options.enableGuidance || false;

        if (this.options.enableViewIn3d) {
            this.viewObjectIn3D = new ViewObjectIn3D(this, viewer, this.options);
        }
    }

    async load() {
        this.viewportsExtension = await this.viewer.loadExtension('Autodesk.AEC.ViewportsExtension');
        this.viewportsExtension.addEventListener(Autodesk.AEC.ViewportsExtension.Events.VIEWPORT_DATA_FETCHED_EVENT, this._onViewportDataFetched);

        this.dropMeTool = new DropMeTool(this.viewer, (...args) => this._onDrop(...args), this);

        this.viewer.addEventListener(av.MODEL_ADDED_EVENT, this._onModelLoaded);
        if (this.viewer.model) {
            this._initUI(this.viewer.model);
        }

        this.viewObjectIn3D?.activate();
        return true;
    }

    unload() {
        this.viewObjectIn3D?.deactivate();

        this.viewportsExtension.removeEventListener(Autodesk.AEC.ViewportsExtension.Events.VIEWPORT_DATA_FETCHED_EVENT, this._onViewportDataFetched);
        this.viewportsExtension = null;

        this.viewer.removeEventListener(av.MODEL_ADDED_EVENT, this._onModelLoaded);
        this.viewer.toolController.setIsLocked(false);
        this._deactivateTool();
        this._destroyUI();
        return true;
    }

    _onViewportDataFetched() {
        // It's ok to assume that there is a single 2D model when using DropMe.
        const model = this.viewer.model;

        if (model) {
            this._onModelLoaded({ model: this.viewer.model });
        }
    }

    setOnDrop(cb) {
        this.onDropOverride = cb;
    }

    _destroyUI() {
        this._showDrop(false);

        if (this.dropMeButton) {
            this.dropMeButton.removeFromParent();
            this.dropMeButton = null;
        }

        this.uiInitialized = false;
    }

    _onDrop(pos, dir, mode, bubbleNode, ignoreCameraGlobalOffset) {
        // after drop action had been triggered, we need to deactivate tool as we are transitioning into another view.
        if (this.teleportOnRelease) {
            this.viewer.toolController.setIsLocked(false);
            this._deactivateTool(true);
        }

        const file_2d_extension = this.viewer.model.getDocumentNode().getInputFileType();
        const file_3d_extension = bubbleNode.getInputFileType();

        avp.analytics.track('viewer.place_me', {
            file_2d_extension,
            file_3d_extension,
        });

        console.log(`DropMe: 3D camera position is ${JSON.stringify(pos)}`);
        console.log(`DropMe: 3D camera direction is ${JSON.stringify(dir)}`);
        this.dispatchEvent({ type: DropMeExtension.DROP_ME_EVENT, pos, dir, mode, bubbleNode, ignoreCameraGlobalOffset });

        if (this.onDropOverride) {
            this.onDropOverride(pos, dir, mode, bubbleNode, ignoreCameraGlobalOffset);
        } else if (this.options.onDrop) {
            this.options.onDrop(pos, dir, mode, bubbleNode, ignoreCameraGlobalOffset);
        } else {
            console.warn('DropMe: No onDrop callback specified ');
        }
    }

    _onModelLoaded(event) {
        if (!this.uiInitialized) {
            this._initUI(event.model);
        } else {
            this._updateUI(event.model);
        }
    }

    _deactivateTool(disableButton) {
        // UI might not be initialized yet. E.g., this can happen if the extensionUnload happens
        // before a model was added. In this case, dropMeButton and guidance UI will not exist anyway.
        if (this.uiInitialized) {
            this._showGuidenaceUI(false);

            this.dropMeButton.setState(disableButton ? avu.Button.State.DISABLED : avu.Button.State.INACTIVE);
        }

        this.viewer.toolController.deactivateTool('drop-me');
    }

    isSupportedModel(model, onlyTopViewPlans = true, useOnlyAecModelDataViewports = false) {
        if (!model?.is2d()) {
            return false;
        }

        const bubbleNode = model.getDocumentNode();

        if (!bubbleNode) {
            return false;
        }

        // Notice that filterAndFlattenViewports returns only the viewports that were already fetched and cached.
        // If other viewports will get added later (from an async provider) - it will trigger a VIEWPORT_DATA_FETCHED_EVENT event and we'll get here again.
        let viewports = this.viewportsExtension.filterAndFlattenViewports(bubbleNode, { onlyTopViewPlans });

        if (useOnlyAecModelDataViewports || this.options.useOnlyAecModelDataViewports) {
            viewports = viewports.filter(viewport => viewport instanceof Autodesk.AEC.ViewportsExtension.AecModelDataViewport);
        }

        return viewports.length;
    }

    async _updateUI(model) {
        const toolbar = this.viewer.getToolbar && this.viewer.getToolbar();
        if (!toolbar) {
            return;
        }

        this.dropMeButton.removeFromParent();

        const controlGroup = toolbar.getControl(this.options.toolbarGroup || av.TOOLBAR.NAVTOOLSID);
        controlGroup.addControl(this.dropMeButton, { index: this.toggleIndex });
        const isSupported = this.isSupportedModel(model);
        this._showDrop(isSupported);
    }

    _initUI(model) {
        // There is no need for this function when getToolbar doesn't exist (headless viewer).
        const toolbar = this.viewer.getToolbar && this.viewer.getToolbar();

        if (!toolbar) {
            return;
        }

        this.dropMeButton = new avu.Button('drop-me-icon');
        this.dropMeButton.icon.innerHTML = createDropMeIcon();

        this.dropMeButton.onClick = () => {
            if (this.dropMeTool.active) {
                this._deactivateTool();
            } else {
                this.dropMeButton.setState(avu.Button.State.ACTIVE);
                // TODO: when we activate tool we might want to forbid interaction with other tools
                this.viewer.toolController.activateTool('drop-me');
                this._showGuidenaceUI();
            }
        };
        this.uiInitialized = true;

        this._updateUI(model);
    }

    _showGuidenaceUI(show = true) {
        if (!this.enableGuidance)
            return;

        if (show) {
            const _document = this.getDocument();
            this.guidenaceUI = _document.createElement('div');
            this.guidenaceUI.className = 'dropme-guidenace-ui';
            this.guidenaceUI.textContent = Autodesk.Viewing.i18n.translate('Click on a location to ground yourself, then select a camera direction');
            this.viewer.container.appendChild(this.guidenaceUI);
        } else {
            if (this.viewer.container.contains(this.guidenaceUI)) {
                this.viewer.container.removeChild(this.guidenaceUI);
            }
        }
    }

    _showDrop(value) {
        if (!this.dropMeButton) {
            return;
        }

        const toolbar = this.viewer.getToolbar();
        const controlGroup = toolbar.getControl(this.options.toolbarGroup || av.TOOLBAR.NAVTOOLSID);

        if (controlGroup && !this.container) {
            this.dropMeButton.setState(value ? avu.Button.State.INACTIVE : avu.Button.State.DISABLED);
        }
        const supportedTooltip = Autodesk.Viewing.i18n.translate('Click a 2D location to view that space in 3D');
        const unsupportedTooltip = Autodesk.Viewing.i18n.translate('Current view is not supported by Place Me');

        this.dropMeButton.setToolTip(value ? supportedTooltip : unsupportedTooltip);
    }
}

const createDropMeIcon = () => {
    return [
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">',
            '<g stroke="currentColor" fill="currentColor">',
                '<path d="M67,0c2.4.5,4.8.9,7.1,1.6C87.5,5.3,96.6,16.6,98,31a37.2,37.2,0,0,1-2.1,16.2c-4.7,13-23.6,48.1-31.6,58.2-.3-.2-.6-.3-.8-.5-11.1-17.6-21.9-35.4-30-54.7-4.6-10.9-4.6-21.7.6-32.3S47.5,2.6,58.3.7l3-.7ZM64,47A11.8,11.8,0,1,0,52.3,34.9,12,12,0,0,0,64,47Z"/>',
                '<path d="M12.8,103.4c1.4-8,6.9-12.3,14-14.6s12-2.9,18.1-4.2c1.1-.2,2.4.5,3.4,1.5-4.5,1.1-9.1,1.8-13.5,3.3a35,35,0,0,0-9.6,5c-4,3-4.3,7.5-.7,10.9a28.7,28.7,0,0,0,9.6,6.2,87.3,87.3,0,0,0,59.9.1,28.2,28.2,0,0,0,8.7-5.2c4.9-4.1,4.8-9.7-.8-12.8S90.7,89.3,85,87.3c-1.6-.6-3.4-.6-5.3-1.7,1.1-.4,2.2-1.2,3.2-1.1,7.8.9,15.5,2.2,22.6,6,12,6.4,13,17.9,2.1,26.1-8.1,6.1-17.6,8.5-27.4,10L69.1,128H59.2l-5.2-.7c-9.1-.8-18-2.6-26.4-6.5s-13.4-8.1-14.9-16.8Z"/>',
            '</g>',
        '</svg>'
    ].join('');
};

namespace.DropMeExtension = DropMeExtension;
DropMeExtension.POSITION_FOUND_EVENT = 'POSITION_FOUND_EVENT';
DropMeExtension.OBJECT_SELECTED_EVENT = 'OBJECT_SELECTED_EVENT';
DropMeExtension.DROP_ME_EVENT = 'DROP_ME_EVENT';
DropMeExtension.DROP_ME_STARTED_EVENT = 'DROP_ME_STARTED_EVENT';

Autodesk.Viewing.theExtensionManager.registerExtension(myExtensionName, DropMeExtension);
